/**
 * Form state and handlers for BulkEditPreparedDropsModal.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  toClickIdStructure,
  toDomainScheme,
  type ShortlinkClickIdStructure,
  type ShortlinkDomainScheme,
} from '@trafficpulse/shared';

import {
  useCreateGatewaySenderTemplate,
  useGatewaySenderTemplates,
  useUpdateGatewaySenderTemplate,
} from '@/hooks';
import {
  filterSenderNamesMax11,
  MAX_SELECTED_TEXTS,
  parseSenderNamesInput,
  parseSmsTextsInput,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import type { GatewayOption, LeadsListOption } from './types';
import type { BulkEditInitialValues as BulkEditInitialValuesType, MixedOptions } from './bulk-edit.types';
import { parseIntervalHHMM } from '@/features/smswiser-campaigns/utils/scheduleInterval';
import {
  initialShortlinkValue,
  shortlinkPick,
  type ClickIdStructureValue,
  type DomainSchemeValue,
} from '@/features/smswiser-campaigns/utils/shortlinkOptionState';

export type DropOptionTriState = boolean | 'mixed' | 'unchanged';

export interface CampaignOption {
  id: number;
  name: string;
}

export type { BulkEditInitialValues } from './bulk-edit.types';

export function useBulkEditPreparedDropsForm(
  countryKey: string,
  count: number,
  gateways: GatewayOption[],
  _campaigns: CampaignOption[], // Passed for API compatibility; campaign UI uses it via BulkRouteAmountSection
  leadsLists: LeadsListOption[],
  senderNames: string[],
  smsTexts: string[],
  onSave: (payload: {
    routeId?: number;
    shortLinkTldValue?: string;
    amount?: number;
    leadsListWiserId?: number;
    wiserId?: number;
    executeAfter?: string | null;
    senderNames?: string[];
    smsTexts?: string[];
    batchAmount?: number | null;
    batchDelay?: number | null;
    forceSend?: boolean;
    aiOptimizationEnabled?: boolean;
    aiOptimizationType?: 'ROI' | 'CTR' | null;
    aiOptimizationBatchAmount?: number | null;
    aiOptimizationMinimalValuePercent?: number | null;
    aiOptimizationSendUntil?: string | null;
    useOnlyNewDomains?: boolean;
    clickIdStructure?: ShortlinkClickIdStructure;
    domainScheme?: ShortlinkDomainScheme;
    staticTagId?: string | null;
  }) => void | Promise<void>,
  onClose: () => void,
  onAddSender?: (name: string) => Promise<void>,
  onAddText?: (texts: string[]) => Promise<void>,
  onDeleteSender?: (name: string) => Promise<void>,
  onDeleteText?: (text: string) => Promise<void>,
  initialValues?: BulkEditInitialValuesType,
  /** When bulk and options differ across drops, enables Clear for each. */
  mixedOptions?: MixedOptions,
  /** When provided, enables Save and Send: saves then sends. */
  onSaveAndSend?: (payload: {
    routeId?: number;
    shortLinkTldValue?: string;
    amount?: number;
    leadsListWiserId?: number;
    wiserId?: number;
    executeAfter?: string | null;
    senderNames?: string[];
    smsTexts?: string[];
    batchAmount?: number | null;
    batchDelay?: number | null;
    forceSend?: boolean;
    aiOptimizationEnabled?: boolean;
    aiOptimizationType?: 'ROI' | 'CTR' | null;
    aiOptimizationBatchAmount?: number | null;
    aiOptimizationMinimalValuePercent?: number | null;
    aiOptimizationSendUntil?: string | null;
    useOnlyNewDomains?: boolean;
    clickIdStructure?: ShortlinkClickIdStructure;
    domainScheme?: ShortlinkDomainScheme;
    staticTagId?: string | null;
  }) => Promise<void>,
) {
  const isSingleEdit = count === 1 && initialValues != null;
  const mixed = mixedOptions;
  const [routeId, setRouteId] = useState<number | ''>(
    isSingleEdit && initialValues!.routeId ? initialValues!.routeId : '',
  );
  const [wiserId, setWiserId] = useState<number | ''>(
    isSingleEdit && initialValues!.wiserId ? initialValues!.wiserId : '',
  );
  const [tldValue, setTldValue] = useState(
    isSingleEdit && initialValues!.shortLinkTldValue ? initialValues!.shortLinkTldValue : '',
  );
  const [amount, setAmount] = useState<number | ''>(
    isSingleEdit && initialValues!.amount != null ? initialValues!.amount : '',
  );
  const [executeAfter, setExecuteAfter] = useState(
    isSingleEdit && initialValues!.executeAfter ? initialValues!.executeAfter.trim() : '',
  );
  const [clearSchedule, setClearSchedule] = useState(false);
  const [scheduleInterval, setScheduleInterval] = useState('');
  const [sendInBatches, setSendInBatches] = useState<boolean | '' | 'mixed' | 'unchanged'>(
    mixed?.sendInBatches ? 'mixed' : isSingleEdit && initialValues?.batchAmount && initialValues?.batchDelay ? true : isSingleEdit ? '' : 'unchanged',
  );
  const [batchAmount, setBatchAmount] = useState<number | ''>(
    isSingleEdit && initialValues?.batchAmount != null ? initialValues.batchAmount : '',
  );
  const [batchDelay, setBatchDelay] = useState<number | ''>(
    isSingleEdit && initialValues?.batchDelay != null
      ? Math.floor(initialValues.batchDelay / 60)
      : '',
  );
  const [forceSend, setForceSend] = useState<DropOptionTriState>(
    mixed?.forceSend ? 'mixed' : isSingleEdit ? (initialValues?.forceSend === true) : 'unchanged',
  );
  const [aiOptimizationEnabled, setAiOptimizationEnabled] = useState(
    isSingleEdit && initialValues?.aiOptimizationEnabled === true,
  );
  const [aiOptimizationType, setAiOptimizationType] = useState<'ROI' | 'CTR' | ''>(
    isSingleEdit && (initialValues?.aiOptimizationType === 'ROI' || initialValues?.aiOptimizationType === 'CTR')
      ? initialValues.aiOptimizationType
      : '',
  );
  const [aiOptimizationBatchAmount, setAiOptimizationBatchAmount] = useState<number | ''>(
    isSingleEdit && initialValues?.aiOptimizationBatchAmount != null ? initialValues.aiOptimizationBatchAmount : '',
  );
  const [aiOptimizationMinimalValuePercent, setAiOptimizationMinimalValuePercent] = useState<number | ''>(
    isSingleEdit && initialValues?.aiOptimizationMinimalValuePercent != null ? initialValues.aiOptimizationMinimalValuePercent : '',
  );
  const [aiOptimizationSendUntil, setAiOptimizationSendUntil] = useState<string>(
    isSingleEdit && initialValues?.aiOptimizationSendUntil
      ? (typeof initialValues.aiOptimizationSendUntil === 'string'
          ? initialValues.aiOptimizationSendUntil
          : new Date(initialValues.aiOptimizationSendUntil).toISOString())
      : '',
  );
  const [useOnlyNewDomains, setUseOnlyNewDomains] = useState<DropOptionTriState>(
    mixed?.useOnlyNewDomains ? 'mixed' : isSingleEdit ? (initialValues?.useOnlyNewDomains === true) : 'unchanged',
  );
  const [clickIdStructure, setClickIdStructure] = useState<ClickIdStructureValue>(() =>
    initialShortlinkValue(isSingleEdit, toClickIdStructure(initialValues?.clickIdStructure), mixed?.clickIdStructure),
  );
  const [domainScheme, setDomainScheme] = useState<DomainSchemeValue>(() =>
    initialShortlinkValue(isSingleEdit, toDomainScheme(initialValues?.domainScheme), mixed?.domainScheme),
  );
  const [staticTagId, setStaticTagId] = useState<string | null | 'unchanged'>(
    mixed?.staticTagId ? 'unchanged' : (isSingleEdit ? (initialValues?.staticTagId ?? null) : 'unchanged'),
  );
  const [leadsListWiserId, setLeadsListWiserId] = useState<number | ''>(
    isSingleEdit && initialValues!.leadsListWiserId ? initialValues!.leadsListWiserId : '',
  );
  const [leadSearch, setLeadSearch] = useState('');
  const [selectedSenderNames, setSelectedSenderNames] = useState<string[]>(
    isSingleEdit && initialValues!.senderNames?.length
      ? Array.from(new Set(initialValues!.senderNames))
      : [],
  );
  const [selectedSmsTexts, setSelectedSmsTexts] = useState<string[]>(
    isSingleEdit && initialValues!.smsTexts?.length
      ? Array.from(new Set(initialValues!.smsTexts))
      : [],
  );
  const [senderSearch, setSenderSearch] = useState('');
  const [textSearch, setTextSearch] = useState('');
  const [showCreative, setShowCreative] = useState(true);
  const [showSenders, setShowSenders] = useState(true);
  const [showTexts, setShowTexts] = useState(true);
  const [showLeads, setShowLeads] = useState(true);
  const [newSenderValue, setNewSenderValue] = useState('');
  const [newTextValue, setNewTextValue] = useState('');
  const [localSenders, setLocalSenders] = useState<string[]>([]);
  const [localTexts, setLocalTexts] = useState<string[]>([]);
  const [perDropSmsTexts, setPerDropSmsTexts] = useState<string[][] | null>(null);
  const [perDropSenderNames, setPerDropSenderNames] = useState<string[][] | null>(null);
  const [pendingHideSenders, setPendingHideSenders] = useState<string[]>([]);
  const [replacedByAiSenders, setReplacedByAiSenders] = useState<string[]>([]);

  const effectiveRouteId = routeId !== '' && routeId > 0 ? routeId : 0;
  const { data: senderTemplates = [] } = useGatewaySenderTemplates(
    countryKey,
    effectiveRouteId as number,
  );
  const createTemplate = useCreateGatewaySenderTemplate(
    countryKey,
    effectiveRouteId as number,
  );
  const updateTemplate = useUpdateGatewaySenderTemplate(
    countryKey,
    effectiveRouteId as number,
  );
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const lastSyncedRef = useRef<string>('');

  useEffect(() => {
    setActiveTemplateId(null);
  }, [countryKey, effectiveRouteId]);

  useEffect(() => {
    if (!activeTemplateId || selectedSenderNames.length === 0) return;
    const sorted = [...selectedSenderNames].sort();
    const key = JSON.stringify(sorted);
    if (key === lastSyncedRef.current) return;
    const t = senderTemplates.find((x) => x.id === activeTemplateId);
    if (!t) return;
    const tm = setTimeout(() => {
      const firstName = selectedSenderNames[0] ?? 'Template';
      const name =
        selectedSenderNames.length > 1
          ? `${firstName} +${selectedSenderNames.length - 1}`
          : firstName;
      updateTemplate.mutate({
        id: activeTemplateId,
        senderNames: selectedSenderNames,
        name,
      });
      lastSyncedRef.current = key;
    }, 400);
    return () => clearTimeout(tm);
  }, [
    activeTemplateId,
    selectedSenderNames,
    senderTemplates,
    updateTemplate,
  ]);

  const sendersList = useMemo(
    () =>
      Array.from(
        new Set([
          ...senderNames,
          ...(initialValues?.senderNames ?? []),
          ...localSenders,
        ]),
      ),
    [senderNames, initialValues?.senderNames, localSenders],
  );

  const handleApplyTemplate = useCallback(
    async (senders: string[]) => {
      setSelectedSenderNames(senders);
      const missing = senders.filter((s) => !sendersList.includes(s));
      if (missing.length > 0) {
        setLocalSenders((prev) => uniqueKeepOrderStrings([...prev, ...missing]));
        for (const name of missing) await onAddSender?.(name);
      }
    },
    [sendersList, onAddSender],
  );

  const handleSelectTemplate = useCallback(
    async (template: { id: string; senderNames: string[] } | null) => {
      if (!template) {
        setActiveTemplateId(null);
        return;
      }
      setActiveTemplateId(template.id);
      lastSyncedRef.current = JSON.stringify(
        [...template.senderNames].sort(),
      );
      await handleApplyTemplate(template.senderNames);
    },
    [handleApplyTemplate],
  );

  const handleSaveAsTemplate = useCallback(async () => {
    if (selectedSenderNames.length === 0 || effectiveRouteId <= 0) return;
    const firstName = selectedSenderNames[0] ?? 'Template';
    const name =
      selectedSenderNames.length > 1
        ? `${firstName} +${selectedSenderNames.length - 1}`
        : firstName;
    await createTemplate.mutateAsync({
      name,
      senderNames: selectedSenderNames,
    });
  }, [selectedSenderNames, effectiveRouteId, createTemplate]);

  /** Available texts: global creative + draft's own saved texts + locally added this session. */
  const textsList = useMemo(
    () => Array.from(new Set([...smsTexts, ...(initialValues?.smsTexts ?? []), ...localTexts])),
    [smsTexts, initialValues?.smsTexts, localTexts],
  );

  const selectedGateway = useMemo(
    () => (routeId !== '' ? gateways.find((g) => g.id === routeId) : null),
    [gateways, routeId],
  );

  const costInfo = useMemo(() => {
    const price = selectedGateway?.pricePerSms;
    if (price == null || Number.isNaN(price)) return null;
    const amt = amount !== '' && amount >= 1 ? amount : 0;
    if (amt <= 0) return { perSms: price, total: null };
    const total = amt * price * count;
    return { perSms: price, perDraft: amt * price, total };
  }, [selectedGateway?.pricePerSms, amount, count]);

  const filteredLeads = useMemo(() => {
    const filtered = leadsLists.filter((l) =>
      l.name.toLowerCase().includes(leadSearch.toLowerCase()),
    );
    return [...filtered].sort((a, b) => {
      const aSel = a.id === leadsListWiserId;
      const bSel = b.id === leadsListWiserId;
      return aSel === bSel ? 0 : aSel ? -1 : 1;
    });
  }, [leadsLists, leadSearch, leadsListWiserId]);

  const filteredSenders = useMemo(
    () => sendersList.filter((s) => s.toLowerCase().includes(senderSearch.toLowerCase())),
    [sendersList, senderSearch],
  );
  const filteredTexts = useMemo(
    () => textsList.filter((t) => t.toLowerCase().includes(textSearch.toLowerCase())),
    [textsList, textSearch],
  );

  const toggleSender = useCallback((name: string) => {
    setSelectedSenderNames((prev) =>
      prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name],
    );
  }, []);

  const toggleText = useCallback((text: string) => {
    setSelectedSmsTexts((prev) =>
      prev.includes(text) ? prev.filter((v) => v !== text)
        : prev.length >= MAX_SELECTED_TEXTS ? prev : [...prev, text],
    );
  }, []);

  const selectAllSenders = useCallback(() => {
    setSelectedSenderNames((prev) =>
      prev.length === filteredSenders.length ? [] : [...filteredSenders],
    );
  }, [filteredSenders]);

  const selectAllTexts = useCallback(() => {
    setSelectedSmsTexts((prev) => {
      const capped = filteredTexts.slice(0, MAX_SELECTED_TEXTS);
      return prev.length >= capped.length ? [] : capped;
    });
  }, [filteredTexts]);

  const handleAddSender = useCallback(async () => {
    if (!onAddSender) return;
    const { senders } = parseSenderNamesInput(newSenderValue);
    const valid = filterSenderNamesMax11(senders);
    if (valid.length === 0) return;
    const newNames = valid.filter((n) => !selectedSenderNames.includes(n));
    if (newNames.length === 0) {
      setNewSenderValue('');
      return;
    }
    setLocalSenders((prev) => uniqueKeepOrderStrings([...prev, ...newNames]));
    setSelectedSenderNames((prev) => uniqueKeepOrderStrings([...prev, ...newNames]));
    setNewSenderValue('');
    for (const name of newNames) await onAddSender(name);
  }, [newSenderValue, onAddSender, selectedSenderNames]);

  const handleAddText = useCallback(async () => {
    if (!onAddText) return;
    const { texts } = parseSmsTextsInput(newTextValue);
    if (texts.length === 0) return;
    const newTexts = texts.filter((t) => !selectedSmsTexts.includes(t));
    if (newTexts.length === 0) {
      setNewTextValue('');
      return;
    }
    setLocalTexts((prev) => uniqueKeepOrderStrings([...prev, ...newTexts]));
    setSelectedSmsTexts((prev) => uniqueKeepOrderStrings([...prev, ...newTexts]));
    setNewTextValue('');
    await onAddText(newTexts);
  }, [newTextValue, onAddText, selectedSmsTexts]);

  const handleSetExecuteAfter = useCallback((v: string) => {
    setExecuteAfter(v);
    if (v.trim()) setClearSchedule(false);
  }, []);

  const handleRemoveSchedule = useCallback(() => {
    setClearSchedule(true);
    setExecuteAfter('');
    setScheduleInterval('');
  }, []);

  const buildPayload = useCallback(() => {
    const payload: Parameters<typeof onSave>[0] = {};
    if (routeId !== '') payload.routeId = routeId;
    if (wiserId !== '') payload.wiserId = wiserId;
    if (tldValue.trim()) payload.shortLinkTldValue = tldValue.trim();
    if (amount !== '' && amount >= 1) payload.amount = amount;
    if (leadsListWiserId !== '') payload.leadsListWiserId = leadsListWiserId;
    if (clearSchedule) {
      payload.executeAfter = null;
    } else if (executeAfter.trim()) {
      payload.executeAfter =
        executeAfter.includes('Z') || executeAfter.includes('+')
          ? executeAfter.trim()
          : new Date(executeAfter).toISOString();
    }
    if (perDropSenderNames != null && perDropSenderNames.length > 0) {
      (payload as { senderNamesPerDrop?: string[][] }).senderNamesPerDrop = perDropSenderNames;
    } else if (selectedSenderNames.length > 0) {
      payload.senderNames = filterSenderNamesMax11(uniqueKeepOrderStrings(selectedSenderNames)).slice(0, 50);
    }
    if (perDropSmsTexts != null && perDropSmsTexts.length > 0) {
      (payload as { smsTextsPerDrop?: string[][] }).smsTextsPerDrop = perDropSmsTexts;
    } else if (selectedSmsTexts.length > 0) {
      payload.smsTexts = uniqueKeepOrderStrings(selectedSmsTexts).slice(0, 50);
    }
    if (sendInBatches === true && batchAmount !== '' && batchDelay !== '') {
      payload.batchAmount = batchAmount as number;
      payload.batchDelay = (batchDelay as number) * 60;
    } else if (isSingleEdit && sendInBatches === false) {
      payload.batchAmount = null;
      payload.batchDelay = null;
    } else if (!isSingleEdit && sendInBatches === false) {
      payload.batchAmount = null;
      payload.batchDelay = null;
    }
    if (isSingleEdit) {
      payload.forceSend = forceSend as boolean;
      payload.aiOptimizationEnabled = aiOptimizationEnabled;
      payload.aiOptimizationType =
        aiOptimizationEnabled && aiOptimizationType ? (aiOptimizationType as 'ROI' | 'CTR') : null;
      payload.aiOptimizationBatchAmount =
        aiOptimizationEnabled && aiOptimizationBatchAmount !== '' ? (aiOptimizationBatchAmount as number) : null;
      payload.aiOptimizationMinimalValuePercent =
        aiOptimizationEnabled && aiOptimizationMinimalValuePercent !== ''
          ? (aiOptimizationMinimalValuePercent as number)
          : null;
      payload.aiOptimizationSendUntil =
        aiOptimizationEnabled && aiOptimizationSendUntil ? aiOptimizationSendUntil : null;
      payload.useOnlyNewDomains = useOnlyNewDomains as boolean;
      payload.staticTagId = staticTagId ?? null;
    } else {
      if (forceSend === true) payload.forceSend = true;
      else if (forceSend === false) payload.forceSend = false;
      if (aiOptimizationEnabled) {
        payload.aiOptimizationEnabled = true;
        if (aiOptimizationType) payload.aiOptimizationType = aiOptimizationType as 'ROI' | 'CTR';
        if (aiOptimizationBatchAmount !== '') payload.aiOptimizationBatchAmount = aiOptimizationBatchAmount as number;
        if (aiOptimizationMinimalValuePercent !== '')
          payload.aiOptimizationMinimalValuePercent = aiOptimizationMinimalValuePercent as number;
        if (aiOptimizationSendUntil) payload.aiOptimizationSendUntil = aiOptimizationSendUntil;
      }
      if (useOnlyNewDomains === true) payload.useOnlyNewDomains = true;
      else if (useOnlyNewDomains === false) payload.useOnlyNewDomains = false;
      if (staticTagId !== 'unchanged' && staticTagId != null) payload.staticTagId = staticTagId;
      else if (staticTagId === null) payload.staticTagId = null;
    }
    // Single edit state is always concrete; bulk 'unchanged'/'mixed' keep each drop's own.
    const pickedClickId = shortlinkPick(clickIdStructure);
    if (pickedClickId) payload.clickIdStructure = pickedClickId;
    const pickedScheme = shortlinkPick(domainScheme);
    if (pickedScheme) payload.domainScheme = pickedScheme;
    return payload;
  }, [
    amount,
    batchAmount,
    batchDelay,
    clearSchedule,
    executeAfter,
    forceSend,
    aiOptimizationEnabled,
    aiOptimizationType,
    aiOptimizationBatchAmount,
    aiOptimizationMinimalValuePercent,
    aiOptimizationSendUntil,
    useOnlyNewDomains,
    clickIdStructure,
    domainScheme,
    staticTagId,
    isSingleEdit,
    leadsListWiserId,
    routeId,
    sendInBatches,
    wiserId,
    selectedSenderNames,
    selectedSmsTexts,
    perDropSmsTexts,
    perDropSenderNames,
    tldValue,
  ]);

  const handleSave = useCallback(async () => {
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) return;
    await onSave(payload);
  }, [buildPayload, onSave]);

  const handleSaveAndSend = useCallback(async () => {
    if (!onSaveAndSend) return;
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) return;
    await onSaveAndSend(payload);
    onClose();
  }, [buildPayload, onClose, onSaveAndSend]);

  const clearSendersSelection = useCallback(() => setSelectedSenderNames([]), []);
  const clearTextsSelection = useCallback(() => setSelectedSmsTexts([]), []);

  const markSendersForHide = useCallback(
    (senders: string[]) => setPendingHideSenders((p) => [...p, ...senders]),
    [],
  );
  const saveGeneratedSendersAndSelectOnly = useCallback((newSenders: string[]) => {
    setLocalSenders((prev) => uniqueKeepOrderStrings([...prev, ...newSenders]));
    setReplacedByAiSenders((prev) => uniqueKeepOrderStrings([...prev, ...selectedSenderNames]));
    setSelectedSenderNames(newSenders.slice(0, 50));
    setPerDropSenderNames(null);
  }, [selectedSenderNames]);

  const [pendingHideTexts, setPendingHideTexts] = useState<string[]>([]);
  const [replacedByAiTexts, setReplacedByAiTexts] = useState<string[]>([]);
  const markTextsForHide = useCallback(
    (texts: string[]) => setPendingHideTexts((p) => [...p, ...texts]),
    [],
  );
  const autoSelectTexts = useCallback((texts: string[]) => {
    setLocalTexts((prev) => uniqueKeepOrderStrings([...prev, ...texts]));
    setSelectedSmsTexts((prev) =>
      uniqueKeepOrderStrings([...prev, ...texts]).slice(0, MAX_SELECTED_TEXTS),
    );
  }, []);

  /** Adds generated texts, selects ONLY them, marks old selection as replaced. */
  const saveGeneratedAndSelectOnly = useCallback((newTexts: string[]) => {
    setLocalTexts((prev) => uniqueKeepOrderStrings([...prev, ...newTexts]));
    setReplacedByAiTexts((prev) => uniqueKeepOrderStrings([...prev, ...selectedSmsTexts]));
    setSelectedSmsTexts(newTexts.slice(0, MAX_SELECTED_TEXTS));
  }, [selectedSmsTexts]);

  const handleDeleteSender = useCallback(
    async (name: string) => {
      await onDeleteSender?.(name);
      setLocalSenders((prev) => prev.filter((s) => s !== name));
    },
    [onDeleteSender],
  );

  const handleDeleteSenders = useCallback(
    async (names: string[]) => {
      if (names.length === 0) return;
      const deleteSet = new Set(names);
      await Promise.all(names.map((name) => onDeleteSender?.(name)));
      setLocalSenders((prev) => prev.filter((s) => !deleteSet.has(s)));
    },
    [onDeleteSender],
  );

  const handleDeleteText = useCallback(
    async (text: string) => {
      await onDeleteText?.(text);
      setLocalTexts((prev) => prev.filter((t) => t !== text));
    },
    [onDeleteText],
  );

  const hasCreativeSelection =
    selectedSenderNames.length > 0 || selectedSmsTexts.length > 0;
  /**
   * AI on with a gap in its settings. The single-edit modal has always blocked
   * Save on this; bulk edit did not, and the server accepted it — which is how
   * drops ended up stored with AI enabled and no settings, and then could never
   * be duplicated (create requires all four). Blocked here so the answer is a
   * disabled button rather than a 400 on a 60-row batch.
   */
  const aiSettingsIncomplete =
    aiOptimizationEnabled &&
    (aiOptimizationType === '' ||
      aiOptimizationBatchAmount === '' ||
      aiOptimizationMinimalValuePercent === '' ||
      aiOptimizationSendUntil === '');
  const hasAnyChange = isSingleEdit
    ? routeId !== '' &&
      tldValue.trim() !== '' &&
      amount !== '' &&
      amount >= 1 &&
      leadsListWiserId !== '' &&
      selectedSenderNames.length > 0 &&
      selectedSmsTexts.length > 0
    : routeId !== '' ||
      wiserId !== '' ||
      tldValue.trim() !== '' ||
      (amount !== '' && amount >= 1) ||
      executeAfter.trim() !== '' ||
      parseIntervalHHMM(scheduleInterval) > 0 ||
      clearSchedule ||
      leadsListWiserId !== '' ||
      (typeof sendInBatches === 'boolean' || sendInBatches === 'mixed') ||
      (typeof sendInBatches === 'boolean' && sendInBatches && batchAmount !== '' && batchDelay !== '') ||
      (perDropSmsTexts != null && perDropSmsTexts.length > 0) ||
      (perDropSenderNames != null && perDropSenderNames.length > 0) ||
      forceSend === true ||
      forceSend === false ||
      aiOptimizationEnabled ||
      (aiOptimizationType && aiOptimizationBatchAmount !== '' && aiOptimizationMinimalValuePercent !== '' && aiOptimizationSendUntil !== '') ||
      useOnlyNewDomains === true ||
      useOnlyNewDomains === false ||
      shortlinkPick(clickIdStructure) !== undefined ||
      shortlinkPick(domainScheme) !== undefined ||
      ((!isSingleEdit && staticTagId !== 'unchanged') || (isSingleEdit && (staticTagId ?? null) !== (initialValues?.staticTagId ?? null))) ||
      hasCreativeSelection;

  return {
    routeId,
    setRouteId,
    wiserId,
    setWiserId,
    sendInBatches,
    setSendInBatches,
    batchAmount,
    setBatchAmount,
    batchDelay,
    setBatchDelay,
    forceSend,
    setForceSend,
    aiOptimizationEnabled,
    setAiOptimizationEnabled,
    aiOptimizationType,
    setAiOptimizationType,
    aiOptimizationBatchAmount,
    setAiOptimizationBatchAmount,
    aiOptimizationMinimalValuePercent,
    setAiOptimizationMinimalValuePercent,
    aiOptimizationSendUntil,
    setAiOptimizationSendUntil,
    useOnlyNewDomains,
    setUseOnlyNewDomains,
    clickIdStructure,
    setClickIdStructure,
    domainScheme,
    setDomainScheme,
    staticTagId,
    setStaticTagId,
    tldValue,
    setTldValue,
    amount,
    setAmount,
    executeAfter,
    setExecuteAfter: handleSetExecuteAfter,
    clearSchedule,
    onRemoveSchedule: handleRemoveSchedule,
    scheduleInterval,
    setScheduleInterval,
    scheduleIntervalMinutes: parseIntervalHHMM(scheduleInterval),
    leadsListWiserId,
    setLeadsListWiserId,
    leadSearch,
    setLeadSearch,
    selectedSenderNames,
    selectedSmsTexts,
    senderSearch,
    setSenderSearch,
    textSearch,
    setTextSearch,
    showCreative,
    setShowCreative,
    showSenders,
    setShowSenders,
    showTexts,
    setShowTexts,
    showLeads,
    setShowLeads,
    newSenderValue,
    setNewSenderValue,
    newTextValue,
    setNewTextValue,
    sendersList,
    textsList,
    costInfo,
    filteredLeads,
    filteredSenders,
    filteredTexts,
    toggleSender,
    toggleText,
    selectAllSenders,
    selectAllTexts,
    handleAddSender,
    handleAddText,
    handleSave,
    handleSaveAndSend,
    hasAnyChange,
    aiSettingsIncomplete,
    clearSendersSelection,
    clearTextsSelection,
    handleDeleteSender,
    handleDeleteSenders,
    handleDeleteText,
    senderTemplates,
    activeTemplateId,
    handleSelectTemplate,
    handleSaveAsTemplate,
    canSaveTemplate: effectiveRouteId > 0,
    autoSelectTexts,
    saveGeneratedAndSelectOnly,
    pendingHideTexts,
    replacedByAiTexts,
    markTextsForHide,
    perDropSmsTexts,
    setPerDropSmsTexts,
    pendingHideSenders,
    replacedByAiSenders,
    markSendersForHide,
    perDropSenderNames,
    setPerDropSenderNames,
    saveGeneratedSendersAndSelectOnly,
  };
}
